// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useMemo } from 'react';
import { Share2 } from 'lucide-react';

const fmt = (n) => Math.round(n).toLocaleString('it-IT');

const CITIES_IT = ['Milano', 'Roma', 'Torino', 'Bologna', 'Firenze', 'Napoli', 'Altra città Nord', 'Altra città Centro', 'Altra città Sud'];

// Cost of living index: Italy (Milano) = 100 base
const COUNTRIES = [
  { code: 'DE', name: 'Germania',       city: 'Berlino',     idx: 108, tax: '42%', rent: 1350, groceries: 280, health: 8.5, welfare: 8.2 },
  { code: 'FR', name: 'Francia',        city: 'Parigi',      idx: 115, tax: '44%', rent: 1500, groceries: 290, health: 8.3, welfare: 8.0 },
  { code: 'NL', name: 'Paesi Bassi',    city: 'Amsterdam',   idx: 120, tax: '49%', rent: 1700, groceries: 300, health: 8.4, welfare: 8.4 },
  { code: 'BE', name: 'Belgio',         city: 'Bruxelles',   idx: 105, tax: '50%', rent: 1200, groceries: 260, health: 8.0, welfare: 7.8 },
  { code: 'CH', name: 'Svizzera',       city: 'Zurigo',      idx: 165, tax: '22%', rent: 2400, groceries: 480, health: 7.5, welfare: 8.6 },
  { code: 'PT', name: 'Portogallo',     city: 'Lisbona',     idx: 78,  tax: '28%', rent: 1100, groceries: 180, health: 7.2, welfare: 7.0 },
  { code: 'AT', name: 'Austria',        city: 'Vienna',      idx: 107, tax: '43%', rent: 1300, groceries: 260, health: 8.6, welfare: 8.3 },
  { code: 'ES', name: 'Spagna',         city: 'Madrid',      idx: 82,  tax: '37%', rent: 1100, groceries: 210, health: 7.8, welfare: 7.5 },
];

const ITALY_IDX = {
  'Milano': 100, 'Roma': 92, 'Torino': 87, 'Bologna': 90, 'Firenze': 93,
  'Napoli': 75, 'Altra città Nord': 88, 'Altra città Centro': 83, 'Altra città Sud': 70,
};

export default function StipendioEuropa() {
  const [stipendio, setStipendio] = useState(2000);
  const [citta, setCitta] = useState('Milano');
  const [famiglia, setFamiglia] = useState('Single');
  const [copied, setCopied] = useState(false);

  const results = useMemo(() => {
    const itIdx = ITALY_IDX[citta] || 100;
    return COUNTRIES.map(c => {
      const equiv = Math.round(stipendio * (c.idx / itIdx));
      const welfare = Math.round((c.welfare * 10) / 10 * 10) / 10;
      return { ...c, equiv, welfare };
    }).sort((a, b) => a.equiv - b.equiv);
  }, [stipendio, citta]);

  const best = results[0];
  const worst = results[results.length - 1];

  const handleShare = () => {
    const text = `Il mio stipendio di €${fmt(stipendio)} in Italia vale €${fmt(best.equiv)} in ${best.name} — scopri il tuo → finsapiens.it/calcolatori/stipendio-europa`;
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div className="space-y-6" data-testid="stipendio-europa-calc">
      {/* Educational box */}
      <div style={{ borderLeft: '3px solid #C9A84C', background: 'rgba(201,168,76,0.07)', padding: '14px 18px', borderRadius: '0 8px 8px 0' }}>
        <p className="text-sm text-[#E8E8E4] leading-relaxed">
          <strong style={{ color: '#C9A84C' }}>Uno stipendio di €2.000 in Italia non ha lo stesso potere d'acquisto di €2.000 in Germania o nei Paesi Bassi.</strong> Il costo della vita, la pressione fiscale e i servizi pubblici cambiano tutto.
        </p>
      </div>

      {/* INPUTS */}
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm text-[#888780] mb-1">Stipendio netto mensile in Italia (€)</label>
          <input type="number" value={stipendio} onChange={e => setStipendio(Number(e.target.value))}
            className="w-full bg-[#252523] border border-white/10 rounded-lg px-4 py-2.5 text-[#E8E8E4] focus:border-[#C9A84C]/50 outline-none"
            data-testid="se-stipendio" />
        </div>
        <div>
          <label className="block text-sm text-[#888780] mb-1">Città italiana di residenza</label>
          <select value={citta} onChange={e => setCitta(e.target.value)}
            className="w-full bg-[#252523] border border-white/10 rounded-lg px-4 py-2.5 text-[#E8E8E4] focus:border-[#C9A84C]/50 outline-none"
            data-testid="se-citta">
            {CITIES_IT.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm text-[#888780] mb-1">Situazione familiare</label>
          <select value={famiglia} onChange={e => setFamiglia(e.target.value)}
            className="w-full bg-[#252523] border border-white/10 rounded-lg px-4 py-2.5 text-[#E8E8E4] focus:border-[#C9A84C]/50 outline-none"
            data-testid="se-famiglia">
            {['Single', 'Coppia senza figli', 'Coppia con figli'].map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
      </div>

      {/* HIGHLIGHT BOXES */}
      <div className="grid md:grid-cols-2 gap-4" data-testid="se-highlights">
        <div style={{ background: 'rgba(61,122,82,0.12)', border: '1px solid rgba(61,122,82,0.3)', borderRadius: 10, padding: '16px 18px' }}>
          <div className="text-xs text-[#888780] mb-1">Il tuo stipendio vale di più in</div>
          <div className="text-xl font-bold text-[#3D7A52]">{best.name} ({best.city})</div>
          <div className="text-sm text-[#E8E8E4] mt-1">Equiv. netto: <strong>€ {fmt(best.equiv)}/mese</strong></div>
          <div className="text-xs text-[#888780] mt-1">Affitto medio: €{fmt(best.rent)}/mese · Spesa: €{fmt(best.groceries)}/sett</div>
        </div>
        <div style={{ background: 'rgba(192,57,43,0.10)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: 10, padding: '16px 18px' }}>
          <div className="text-xs text-[#888780] mb-1">Stipendio necessario in</div>
          <div className="text-xl font-bold text-[#C0392B]">{worst.name} ({worst.city})</div>
          <div className="text-sm text-[#E8E8E4] mt-1">Dovresti guadagnare <strong>€ {fmt(worst.equiv)}/mese</strong></div>
          <div className="text-xs text-[#888780] mt-1">Affitto medio: €{fmt(worst.rent)}/mese · Spesa: €{fmt(worst.groceries)}/sett</div>
        </div>
      </div>

      {/* COMPARISON TABLE */}
      <div data-testid="se-comparison-table">
        <h3 className="text-sm font-medium text-[#E8E8E4] mb-3">Confronto 8 paesi europei — basato su Numbeo / Eurostat 2026</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <th className="text-left py-2 px-3 text-[#888780] font-normal">Paese</th>
                <th className="text-right py-2 px-3 text-[#888780] font-normal">Stipendio equiv.</th>
                <th className="text-right py-2 px-3 text-[#888780] font-normal">Affitto città</th>
                <th className="text-right py-2 px-3 text-[#888780] font-normal">Costo vita</th>
                <th className="text-right py-2 px-3 text-[#888780] font-normal">Benessere</th>
              </tr>
            </thead>
            <tbody>
              {results.map((c, i) => (
                <tr key={c.code} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: i === 0 ? 'rgba(61,122,82,0.07)' : i === results.length - 1 ? 'rgba(192,57,43,0.05)' : i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent' }}>
                  <td className="py-2.5 px-3 text-[#E8E8E4]">
                    {i === 0 && <span className="text-xs mr-1">🏆</span>}
                    {i === results.length - 1 && <span className="text-xs mr-1">💸</span>}
                    <strong>{c.name}</strong> <span className="text-[#888780]">({c.city})</span>
                  </td>
                  <td className="py-2.5 px-3 text-right font-medium text-[#C9A84C]">€ {fmt(c.equiv)}</td>
                  <td className="py-2.5 px-3 text-right text-[#888780]">€{fmt(c.rent)}</td>
                  <td className="py-2.5 px-3 text-right text-[#888780]">Indice {c.idx}</td>
                  <td className="py-2.5 px-3 text-right text-[#888780]">{c.welfare}/10</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* SHARE BUTTON */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleShare}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:opacity-80"
          style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#C9A84C' }}
          data-testid="se-share-btn"
        >
          <Share2 className="w-4 h-4" />
          {copied ? 'Copiato!' : 'Condividi il risultato'}
        </button>
      </div>

      <p className="text-xs text-[#888780] italic">Dati basati su indici Numbeo e Eurostat 2026. Valori indicativi — il costo della vita varia significativamente tra città diverse dello stesso paese.</p>
</div>
  );
}
